import React, { useState, useEffect } from 'react';
import { Modal, Button, Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { faBtc } from '@fortawesome/free-brands-svg-icons';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../config/featureFlags';

const GroupAssetModal = ({ show, onHide, asset, groupId, onAssetAdded }) => {
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
    const barterEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceBarter);
    const sheafEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase);
    const [formData, setFormData] = useState({
        askingCurrency: 'BTC',
        id: asset ? asset.id : 0,
        ownedByGroupId: groupId,
        ownedByUserId: asset,
        assetName: asset ? asset.assetName : '',
        assetDescription: asset ? asset.assetDescription : '',
        productName: asset ? asset.productName : '',
        productDescription: asset ? asset.productDescription : '',
        quantity: asset ? asset.quantity : 0,
        availability: asset ? asset.availability : true,
        qrCode: asset ? asset.qrCode : '',
        location: asset ? asset.location : '',
        acquisitionDate: asset ? asset.acquisitionDate : new Date().toISOString(), 
        checkOutable: asset ? asset.checkOutable : false,
        checkedOut: asset ? asset.checkedOut : '',
        checkedIn: asset ? asset.checkedIn : '',
        checkedOutBy: asset ? asset.checkedOutBy : '',
        lastUpdated: asset ? asset.lastUpdated : new Date().toISOString(),
        tradable: asset ? asset.tradable : false,
        forSale: asset ? asset.forSale : false,
        salePrice: asset ? asset.salePrice : '',
        isDeleted: asset ? asset.isDeleted : false,
        acceptsSheaf: asset ? asset.acceptsSheaf : false,
        sheafPrice: asset ? asset.sheafPrice : '',
        AssetImage: null,
    });

    //BTC Conversions
    useEffect(() => {
        // Fetch conversion rate when component mounts
        if (bitcoinEnabled) fetchConversionRate();
    }, [bitcoinEnabled]);

    const fetchConversionRate = async () => {
        try {
            const response = await fetch(
                'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
            );

            if (!response.ok) {
                throw new Error('Failed to fetch conversion rate');
            }

            const data = await response.json();
            const rate = data.bitcoin.usd;

            setFormData((prevData) => ({
                ...prevData,
                conversionRate: rate,
            }));
        } catch (error) {
            console.error('Error fetching conversion rate:', error);
        }
    };

    const handleCurrencySwitch = () => {
        const { salePrice, conversionRate, askingCurrency } = formData;
        const convertedPrice = askingCurrency === 'BTC' ? salePrice * conversionRate : salePrice / conversionRate;

        setFormData(prevData => ({
            ...prevData,
            salePrice: convertedPrice,
            askingCurrency: askingCurrency === 'USD' ? 'BTC' : 'USD',
        }));
    };
    //===============
    const handleInputChange = (e) => {
        const { name, value, files, type, checked } = e.target;

        if (name === 'AssetImage') {
            setFormData((prevData) => ({
                ...prevData,
                AssetImage: files[0],
            }));
        } else if (type === 'checkbox') {
            setFormData((prevData) => ({
                ...prevData,
                [name]: checked, // Use the checked property for checkboxes
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    const handleCloseModal = () => {
        onHide();
        if (onAssetAdded) {
            onAssetAdded(); 
        }
    };

    const handleSave = async () => {
        try {
            const formDataToSend = new FormData();

            const askingPriceInBTC =
                formData.askingCurrency === 'USD'
                    ? formData.salePrice / formData.conversionRate
                    : formData.salePrice;



            // Append existing data
            Object.entries(formData).forEach(([key, value]) => {
                if (key === 'AssetImage' && value) {
                    formDataToSend.append('AssetImage', value);
                } else if (key === 'salePrice') {
                    formDataToSend.append('salePrice', askingPriceInBTC);
                } else {
                    formDataToSend.append(key, value);
                }
            });


            // Save logic here using the formData
            //const response = await saveAssetToAPI(formDataToSend);

            // After successful save, you can close the modal
            onHide();
        } catch (error) {
            console.error('Error saving asset:', error);
            // Handle error appropriately
        }
    };

    return (
        <Modal show={show} onHide={handleCloseModal} className="tyk-modal" centered size="lg">
            <Modal.Header closeButton closeVariant="white">
                <div>
                    <span className="tyk-kicker">Group inventory</span>
                    <Modal.Title>{asset ? 'Edit Asset' : 'Add Asset'}</Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Form className="tyk-panel">
                    <div className="tyk-grid mb-3">
                    <Form.Group controlId="formAssetName">
                        <Form.Label>Asset name</Form.Label>
                        <Form.Control
                            className="tyk-input"
                            type="text"
                            placeholder="Enter asset name"
                            name="assetName"
                            value={formData.assetName}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="formAssetDescription">
                        <Form.Label>Asset description</Form.Label>
                        <Form.Control
                            className="tyk-textarea"
                            as="textarea"
                            placeholder="Enter asset description"
                            name="assetDescription"
                            value={formData.assetDescription}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    </div>
                    <div className="tyk-grid mb-3">
                    <Form.Group controlId="formQuantity">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                            className="tyk-input"
                            type="number"
                            placeholder="Enter quantity"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="formLocation">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                            className="tyk-input"
                            type="text"
                            placeholder="Enter location"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    </div>
                    <Row className="g-3 mb-3">
                        {barterEnabled && <Col>
                            <Form.Group controlId="formCheckOutable">
                                <Form.Check
                                    type="checkbox"
                                    label="Check-Outable"
                                    name="checkOutable"
                                    checked={formData.checkOutable}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>}
                        {bitcoinEnabled && <Col>
                            <Form.Group controlId="formTradable">
                                <Form.Check
                                    type="checkbox"
                                    label="Tradable"
                                    name="tradable"
                                    checked={formData.tradable}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>}
                        <Col>
                            <Form.Group controlId="formForSale">
                                <Form.Check
                                    type="checkbox"
                                    label="For Sale"
                                    name="forSale"
                                    checked={formData.forSale}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {bitcoinEnabled && formData.forSale && (
                        <Form.Group controlId="formSalePrice" className="mb-3">
                            <Form.Label>Asking Price</Form.Label>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <Form.Control
                                    className="tyk-input"
                                    type="number"
                                    placeholder="Enter sale price"
                                    name="salePrice"
                                    value={formData.salePrice}
                                    onChange={handleInputChange}
                                />
                                <Form.Group controlId="currencySwitch" className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faBtc} className="mr-2" />
                                    <Form.Switch
                                        onChange={handleCurrencySwitch}
                                    />
                                    <FontAwesomeIcon icon={faDollarSign} className="ml-2" />
                                </Form.Group>
                            </div>
                        </Form.Group>
                    )}
                    {sheafEnabled && <Form.Group controlId="formAcceptsSheaf">
                        <Form.Check
                            type="checkbox"
                            label="Accepts Sheaf"
                            name="acceptsSheaf"
                            checked={formData.acceptsSheaf}
                            onChange={handleInputChange}
                        />
                    </Form.Group>}
                    {sheafEnabled && formData.acceptsSheaf && (
                        <Form.Group controlId="formSheafPrice" className="mb-3">
                            <Form.Label>Sheaf Price</Form.Label>
                            <Form.Control
                                className="tyk-input"
                                type="number"
                                placeholder="Enter Sheaf price"
                                name="sheafPrice"
                                value={formData.sheafPrice}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    )}
                    {(formData.forSale || formData.tradable) && (
                        <>
                            <div className="tyk-grid mb-3">
                            <Form.Group controlId="formProductName">
                                <Form.Label>Marketplace title</Form.Label>
                                <Form.Control
                                    className="tyk-input"
                                    type="text"
                                    placeholder="Enter product name"
                                    name="productName"
                                    value={formData.productName}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="formProductDescription">
                                <Form.Label>Marketplace description</Form.Label>
                                <Form.Control
                                    className="tyk-textarea"
                                    as="textarea"
                                    placeholder="Enter product description"
                                    name="productDescription"
                                    value={formData.productDescription}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            </div>
                        </>
                    )}
                    <Form.Group controlId="formAssetImage" className="mb-3">
                        <Form.Label>Asset Image</Form.Label>
                        <Form.Control
                            className="tyk-input"
                            type="file"
                            id="custom-file"
                            label="Choose file"
                            custom
                            name="AssetImage"
                            onChange={handleInputChange}
                        />
                    </Form.Group>

                
                    <Button variant="primary" onClick={handleSave} className="tyk-action">
                        Save
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default GroupAssetModal;
